import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, X } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Pick a GlobeMed specialty instead of typing one.
 *
 * The catalogue is 110 entries, so a plain <select> is unusable and a text
 * input is worse: the value has to match GlobeMed's string exactly or the
 * hospital's routing rule for it never fires. A type-ahead combobox is the
 * only shape that stays quick at 110 while guaranteeing an exact value.
 *
 * Free text is still accepted and preserved — an intake case can name
 * something the catalogue doesn't have, and silently dropping what staff typed
 * would be worse than an unmatched specialty. It's just no longer the only
 * way in.
 */
export function SpecialtyPicker({
  value,
  onChange,
  placeholder = 'Search specialties…',
  dir,
}: {
  value: string;
  onChange: (description: string, code: string | null) => void;
  placeholder?: string;
  dir?: 'rtl' | 'ltr';
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { data: specialties = [] } = useQuery({
    queryKey: ['specialties'],
    queryFn: () => api.hospitals.listSpecialties(),
    staleTime: 60 * 60 * 1000,
  });

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return specialties;
    return specialties.filter(
      (s) => s.description.toLowerCase().includes(q) || s.code.includes(q),
    );
  }, [specialties, query]);

  // Close on an outside click — a combobox that traps focus is worse than one
  // that gets out of the way.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => setActive(0), [query]);

  // Keep the keyboard-highlighted row in view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (description: string, code: string | null) => {
    onChange(description, code);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return Math.max(0, Math.min(matches.length - 1, next));
      });
    } else if (e.key === 'Enter') {
      if (open && matches[active]) {
        e.preventDefault();
        choose(matches[active].description, matches[active].code);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls="specialty-listbox"
          aria-autocomplete="list"
          value={open ? query : value}
          placeholder={value || placeholder}
          dir={dir}
          onFocus={() => { setQuery(''); setOpen(true); }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // Typing IS the value until something is picked, so a specialty the
            // catalogue lacks still reaches the case — just without a code.
            onChange(e.target.value, null);
          }}
          onKeyDown={onKeyDown}
          className="h-10 w-full rounded-lg border border-border bg-background pe-16 ps-3 text-sm outline-none focus:border-primary"
        />
        <div className="absolute end-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {value && (
            <button
              type="button"
              aria-label="Clear specialty"
              onClick={() => { onChange('', null); setQuery(''); }}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            aria-label="Show specialties"
            onClick={() => { setQuery(''); setOpen((v) => !v); }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <ul
          id="specialty-listbox"
          role="listbox"
          ref={listRef}
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-card py-1 shadow-lg"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              Nothing in GlobeMed's list matches “{query}”. It will be saved as typed.
            </li>
          ) : (
            matches.map((s, i) => {
              const selected = s.description === value;
              return (
                <li
                  key={s.code}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); choose(s.description, s.code); }}
                  className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm ${
                    i === active ? 'bg-primary/10' : ''
                  }`}
                >
                  <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                    {s.code}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{s.description}</span>
                  {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
